



















































import isImage from 'is-image'
import Vue from 'vue'
import { Component, Prop, Ref, VModel } from 'vue-property-decorator'

import ZoomableImage from '../ZoomableImage/ZoomableImage.vue'
import FilesList from './FilesList/FilesList.vue'
import FilesListRowScroll from './FilesList/FilesListRowScroll.vue'
import FileApi from './logic/file-api'
import { FileData, FileStatus, FileType } from './types'

const FileDataFabric = {
  create(
    data: {
      name: string
      preview: string | ArrayBuffer | null
      // eslint-disable-next-line id-blacklist
      file?: File
      descriptor?: string
      type: string
      status: FileStatus
    },
  ): FileData {
    const fileData: any = {
      name: data.name,
      status: data.status,
      preview: data.preview,
      type: data.type,
    }

    if (data.file) {
      // eslint-disable-next-line id-blacklist
      fileData.file = data.file
    }

    if (data.descriptor) {
      fileData.descriptor = data.descriptor
    }

    return fileData
  },
}

@Component({
  components: {
    ZoomableImage,
    FilesList,
    FilesListRowScroll,
  },
})
export default class FileUploaderTargets extends Vue {
  @VModel({ type: String }) fileDescriptors!: string

  @Ref('fileInput') readonly fileInput!: HTMLInputElement

  @Prop() readonly baseApiUrl!: string

  @Prop() readonly availableMimeTypes!: Array<string>

  @Prop() readonly uploadType!: FileType | undefined

  @Ref('droppable-zone') readonly droppableZone!: HTMLElement

  @Prop() readonly multiple!: boolean

  @Prop() readonly disabled!: boolean

  @Prop({ type: Function, required: true }) targetToUpload!: () => { target: string, target_id: string }

  private api: FileApi | null = null

  private isDragoverListenerActivated = false

  public filesData: Array<FileData> = []

  public isOnDroppableZone = false

  getTelegramType(mimeType: string): string {
    if (this.uploadType !== undefined) {
      return this.uploadType
    }
    if (mimeType.includes('image') && !mimeType.includes('image/gif')) {
      return FileType.Image
    }
    return ''
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public onFileInputChange(newFiles: Array<File>): void {
    for (const newFile of newFiles) {
      const reader = new FileReader()

      // eslint-disable-next-line complexity,max-statements
      reader.addEventListener('load', () => {
        const fileData = FileDataFabric.create({
          name: newFile.name,
          status: FileStatus.Set,
          preview: isImage(newFile.name) ? reader.result : '',
          file: newFile,
          type: this.getTelegramType(newFile.type),
        })

        if (!this.multiple && this.filesData.length) {
          this.showNotify()
          return
        }
        if (this.filesData.length >= 10) {
          this.showNotify()
          return
        }
        this.filesData.push(fileData)

        if (this.availableMimeTypes) {
          if (this.availableMimeTypes.includes(newFile.type)) {
            this.uploadFile(fileData)
          } else {
            fileData.status = FileStatus.InvalidMimeType
          }
        } else {
          this.uploadFile(fileData)
        }
      }, false)
      reader.readAsDataURL(newFile)
    }
  }

  getQueryParams() {
    return {
      target: this.targetToUpload().target_id,
    }
  }

  public uploadFile(fileToUploadData: FileData): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.api && fileToUploadData.file) {
        fileToUploadData.status = FileStatus.Loading

        const formData: FormData = new FormData()
        const target = Object.entries(this.getQueryParams())[0]

        formData.append('file', fileToUploadData.file, fileToUploadData.file.name)
        formData.append(target[0], target[1] as string)
        formData.append('type', fileToUploadData.type!)

        this.api.uploadFile(formData)
          .then(({ data }: { data: { file_descriptor: string } }) => {
            fileToUploadData.status = FileStatus.Uploaded
            fileToUploadData.descriptor = data.file_descriptor
            this.input()

            resolve(true)
          })
          .catch((error) => {
            fileToUploadData.status = FileStatus.Failed
            reject(error)
          })
      }
    })
  }

  public removeFile(fileIndex: number): void {
    this.filesData.splice(fileIndex, 1)
    this.input()
  }

  public removeAllFiles(): void {
    this.filesData = []
    this.input()
  }

  public input(): void {
    if (this.filesData.length && this.filesData[0].descriptor) {
      this.fileDescriptors = this.filesData[0].descriptor
    } else {
      this.fileDescriptors = ''
    }
  }

  public getFilesFromDescriptors(descriptor: string): FileData {
    return {
      name: 'File',
      preview: this.api!.getFilePreviewUrl(descriptor, this.getQueryParams().target),
      status: FileStatus.Uploaded,
      descriptor: descriptor,
      type: this.uploadType,
    }
  }

  public mounted(): void {
    this.api = new FileApi(this.baseApiUrl)

    if (this.fileDescriptors) {
      this.filesData = [ this.getFilesFromDescriptors(this.fileDescriptors) ]
    } else {
      this.filesData = []
    }

    this.$forceUpdate()
  }

  // eslint-disable-next-line max-statements
  public updated(): void {
    if (!this.isDragoverListenerActivated) {
      const DRAGOVER_ACTIVE_CLASS = 'dragover-active'

      const addActiveClass = (): void => {
        this.droppableZone.classList.add(DRAGOVER_ACTIVE_CLASS)
        this.isOnDroppableZone = true
      }

      const removeActiveClass = (): void => {
        this.droppableZone.classList.remove(DRAGOVER_ACTIVE_CLASS)
        this.isOnDroppableZone = false
      }

      this.droppableZone.addEventListener('dragenter', addActiveClass, false)
      this.droppableZone.addEventListener('dragover', addActiveClass, false)

      this.droppableZone.addEventListener('dragleave', removeActiveClass, false)
      this.droppableZone.addEventListener('dragend', removeActiveClass, false)
      this.droppableZone.addEventListener('drop', removeActiveClass, false)

      this.droppableZone.addEventListener('drop', (e) => {
        this.droppableZone.classList.remove(DRAGOVER_ACTIVE_CLASS)
        if (e.dataTransfer !== null) {
          this.fileInput.files = e.dataTransfer.files
        }
      }, false)

      this.isDragoverListenerActivated = true
    }
  }

  showNotify(): void {
    this.$notification.success({
      message: this.$t('file_uploader_limit_alert_title').toString(),
      description: this.$t('file_uploader_limit_alert_text').toString(),
      placement: 'bottomRight',
      class: 'successNotification',
    })
  }
}
